import { Canvas, FabricImage, FabricObject, Pattern, Rect } from 'fabric';
import { findObject } from './walk-objects';

/**
 * Canonical `pieceId` values for editor-managed pieces. Add new entries here
 * instead of using inline string literals; the `Piece` type below derives
 * automatically so consumers stay typed without manual maintenance.
 */
export const PIECE = {
  BACKGROUND: 'background',
  ALPHA_FALLOFF: 'alpha-falloff'
} as const;

export type Piece = (typeof PIECE)[keyof typeof PIECE];

/** First canvas object whose `pieceId` matches, or `null`. Descends into Groups. */
export function findByPieceId(canvas: Canvas, pieceId: string): FabricObject | null {
  return findObject(canvas, o => o.pieceId === pieceId) ?? null;
}

export function isBackgroundPiece(obj: FabricObject): boolean {
  return obj.pieceId === PIECE.BACKGROUND;
}

/**
 * System pieces are editor-managed objects — the common panels (settings,
 * effects, delete) skip them. Backgrounds, effect overlays, runtime overlays.
 */
export function isSystemPiece(obj: FabricObject): boolean {
  return obj.isOverlay === true || obj.pieceId === PIECE.ALPHA_FALLOFF || isBackgroundPiece(obj);
}

/**
 * Single source of truth for "objects shown in the layers panel and the
 * timeline". Excludes guidelines (runtime-only) and system pieces.
 */
export function isLayerObject(obj: FabricObject): boolean {
  return !obj.isGuideline && !isSystemPiece(obj);
}

// What makes an object behave as a background.
export const BACKGROUND_CONFIG = {
  selectable: false,
  evented: false,
  hasControls: false,
  hasBorders: false
} as const;

export function ensureBackgroundConfig(obj: FabricObject): void {
  obj.set(BACKGROUND_CONFIG);
}

/**
 * Centralized constructor for backgrounds. Never build the Rect inline elsewhere
 * — every invariant (pieceId, top-left origin, non-interactive) lives here.
 * Fabric's default origin is `center`, which would push half the Rect off-screen.
 */
export function createBackgroundRect(designWidth: number, designHeight: number, fill: string | Pattern): FabricObject {
  const bg = new Rect({
    left: 0,
    top: 0,
    originX: 'left',
    originY: 'top',
    width: designWidth,
    height: designHeight,
    fill,
    strokeWidth: 0,
    ...BACKGROUND_CONFIG
  });
  (bg as FabricObject & { pieceId?: string }).pieceId = PIECE.BACKGROUND;
  return bg;
}

/** Resizes an image-based background to fit the frame, centered. */
export function fitImageBackground(bg: FabricImage, designWidth: number, designHeight: number): void {
  const el = bg.getElement() as HTMLImageElement;
  bg.set({
    scaleX: designWidth / el.width,
    scaleY: designHeight / el.height,
    left: designWidth / 2,
    top: designHeight / 2
  });
}
